import { useTheme } from '@emotion/react';
import { Box, Button, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Skeleton, Switch, Typography } from '@mui/material';
import { useContext, useEffect, useMemo, useState } from 'react';
import { AgendaContext } from '../../../contexts/AgendaContext.jsx';
import { useError } from '../../../contexts/ErrorProvider.jsx';
import CustomSnackbar from '../../common/CustomSnackbar.jsx';

function SeccionPreferenciasNotificacion({ consultorio }) {
  const theme = useTheme();
  const { handleError } = useError();
  const { prestadorSesion, getPreferenciaNotificacion, updatePreferenciaNotificacion } =
    useContext(AgendaContext);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferencias, setPreferencias] = useState({
    recibirCopiaConfirmacion: false,
    recibirCopiaCancelacion: false,
    recibirAgenda: 1,
    audUsuarioCambio: `Prestador - ${prestadorSesion[0]?.nombre}`
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const claveOsde = consultorio?.consultorioOsde?.[0]?.consultorioId;

  const cuit = useMemo(() => {
    if (!prestadorSesion || !claveOsde) {
      return null;
    }

    if (Array.isArray(prestadorSesion)) {
      const prestadorEncontrado = prestadorSesion.find((prestador) =>
        prestador.consultorios?.some((consultorioItem) =>
          consultorioItem.consultorioOsde?.some((osde) => osde.consultorioId === claveOsde)
        )
      );
      return prestadorEncontrado?.identificador || null;
    }

    return prestadorSesion.identificador || null;
  }, [prestadorSesion, claveOsde]);

  useEffect(() => {
    const fetchPreferencias = async () => {
      if (!claveOsde || !cuit) {
        setLoading(false);
        return;
      }

      try {
        const response = await getPreferenciaNotificacion(claveOsde, cuit);
        setPreferencias({
          recibirCopiaConfirmacion: !!response?.recibirCopiaConfirmacion,
          recibirCopiaCancelacion: !!response?.recibirCopiaCancelacion,
          recibirAgenda: response?.recibirAgenda || 1,
          audUsuarioCambio: `Prestador - ${prestadorSesion[0]?.nombre}`
        });
      } catch (error) {
        handleError({ error, description: 'genericError' });
      } finally {
        setLoading(false);
      }
    };

    fetchPreferencias();
  }, [claveOsde, cuit]);

  const handleToggle = (field) => (_, checked) => {
    setPreferencias((current) => ({
      ...current,
      [field]: checked
    }));
  };

  const handleAgendaChange = (e) => {
    setPreferencias((current) => ({ ...current, recibirAgenda: e.target.value }));
  };

  const handleGuardar = async () => {
    if (!claveOsde || !cuit) {
      return;
    }

    setSaving(true);
    try {
      const response = await updatePreferenciaNotificacion(claveOsde, cuit, preferencias);
      setPreferencias({
        recibirCopiaConfirmacion: !!response?.recibirCopiaConfirmacion,
        recibirCopiaCancelacion: !!response?.recibirCopiaCancelacion,
        recibirAgenda: response?.recibirAgenda || 1
      });
      setSnackbarMessage('Preferencias guardadas correctamente.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      handleError({ error, description: 'genericError' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: 'auto',
          border: `2px solid ${theme.palette.backgroundSecondary.main}`,
          borderRadius: '1rem',
          gap: '1.5rem',
          padding: '1.5rem',
          flexDirection: 'column'
        }}>
        <Skeleton variant="text" width="40%" height={36} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <Skeleton variant="rounded" width="55%" height={28} />
          <Skeleton variant="rounded" width="55%" height={28} />
          <Skeleton variant="rounded" width="45%" height={56} />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Skeleton variant="rounded" width={170} height={36} />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        height: 'auto',
        border: `2px solid ${theme.palette.backgroundSecondary.main}`,
        borderRadius: '1rem',
        gap: '1.5rem',
        padding: '1.5rem',
        flexDirection: 'column'
      }}>
      <Typography variant="h4">Preferencias de notificación</Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <FormControlLabel
          control={
            <Switch
              checked={preferencias.recibirCopiaConfirmacion}
              onChange={handleToggle('recibirCopiaConfirmacion')}
            />
          }
          label="Recibir copia de confirmación"
        />
        <FormControlLabel
          control={
            <Switch
              checked={preferencias.recibirCopiaCancelacion}
              onChange={handleToggle('recibirCopiaCancelacion')}
            />
          }
          label="Recibir copia de cancelación"
        />
        <FormControl sx={{ minWidth: 280, mt: 1 }}>
          <InputLabel id="recibir-agenda-label">Recibir agenda</InputLabel>
          <Select
            labelId="recibir-agenda-label"
            value={preferencias.recibirAgenda}
            label="Recibir agenda"
            description="Notificacion por email de su agenda diaria."
            onChange={handleAgendaChange}>
            <MenuItem value={1}>No recibir</MenuItem>
            <MenuItem value={2}>Solo días con horarios configurados</MenuItem>
            <MenuItem value={3}>Todos los días</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={handleGuardar} disabled={saving}>
          Guardar preferencias
        </Button>
      </Box>

      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
    </Box>
  );
}

export default SeccionPreferenciasNotificacion;
